import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Heart } from "lucide-react";

const OPTION_CARD_STYLE = (selected) =>
  `w-full p-3 text-left border-2 rounded-lg transition-colors ${selected ? "border-sky-500 bg-sky-50" : "border-slate-200 hover:border-slate-300"}`;

export default function CustomerExperienceSection({ decision, updateDecision }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* In-flight Experience */}
        <Card>
          <CardHeader className="card-header-flight-deck">
            <CardTitle className="flex items-center gap-2"><Heart className="w-5 h-5" /> {t("decisions.inFlightExperience")}</CardTitle>
          </CardHeader>
          <CardContent className="pt-4 space-y-5">
            <OptionGroup label="In-flight Entertainment" field="inflight_entertainment" decision={decision} updateDecision={updateDecision} options={[
              { value: "none", label: "None", cost: "$0/qtr", desc: "No IFE" },
              { value: "basic", label: "Basic", cost: "$100K/qtr", desc: "+3 customer satisfaction" },
              { value: "premium", label: "Premium", cost: "$300K/qtr", desc: "+8 customer satisfaction" },
            ]} />
            <OptionGroup label="Seat Comfort" field="seat_comfort" decision={decision} updateDecision={updateDecision} options={[
              { value: "standard", label: "Standard", cost: "$0/qtr", desc: "Standard seating" },
              { value: "enhanced", label: "Enhanced", cost: "$200K/qtr", desc: "+4 quality rating" },
              { value: "premium", label: "Premium", cost: "$600K/qtr", desc: "+10 quality rating" },
            ]} />
          </CardContent>
        </Card>

        {/* Ground Experience */}
        <Card>
          <CardHeader className="card-header-flight-deck">
            <CardTitle>{t("decisions.groundExperience")}</CardTitle>
          </CardHeader>
          <CardContent className="pt-4 space-y-5">
            <OptionGroup label="Check-in Experience" field="checkin_experience" decision={decision} updateDecision={updateDecision} options={[
              { value: "manual", label: "Manual", cost: "$0", desc: "Counter check-in only" },
              { value: "self_service", label: "Self-Service", cost: "$150K/qtr", desc: "+reliability, +satisfaction" },
              { value: "full_digital", label: "Full Digital", cost: "$400K/qtr", desc: "+5 satisfaction" },
            ]} />
            <OptionGroup label="Baggage Handling" field="baggage_handling" decision={decision} updateDecision={updateDecision} options={[
              { value: "low", label: "Low Priority", cost: "$0", desc: "Minimal investment" },
              { value: "standard", label: "Standard", cost: "$100K/qtr", desc: "+quality" },
              { value: "high", label: "High Priority", cost: "$250K/qtr", desc: "+3 quality" },
            ]} />
            <OptionGroup label="Customer Service Team" field="customer_service_team" decision={decision} updateDecision={updateDecision} options={[
              { value: "minimal", label: "Minimal", cost: "$50K/qtr", desc: "Basic support" },
              { value: "standard", label: "Standard", cost: "$150K/qtr", desc: "+satisfaction" },
              { value: "dedicated", label: "Dedicated", cost: "$350K/qtr", desc: "+4 satisfaction" },
            ]} />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function OptionGroup({ label, field, decision, updateDecision, options }) {
  const current = decision[field] || options[0]?.value;
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <div className="space-y-1.5 mt-1.5">
        {options.map(opt => (
          <button key={opt.value} onClick={() => updateDecision(field, opt.value)} className={OPTION_CARD_STYLE(current === opt.value)} data-testid={`${field}-${opt.value}`}>
            <div className="flex justify-between"><span className="font-medium text-sm">{opt.label}</span><span className="text-xs font-mono text-slate-500">{opt.cost}</span></div>
            <p className="text-xs text-slate-500">{opt.desc}</p>
          </button>
        ))}
      </div>
    </div>
  );
}
